<template>
    <!--
        暂时去除loading tips
        :pt-loading-tips="loadingTips && JSON.stringify(loadingTips)"
    -->
    <div
        id="insight"
        ref="InsightWrap"
        v-loading="visibleIframe && insightState.iframeLoading"
        :class="[
            $style.insight,
            visibleIframe && $style.visible,
            loadIframe && !insightState.iframeLoading && $style.loaded
        ]"
    >
        <div
            v-if="insightState.maskTheme"
            :class="[$style.mask, insightState.maskTheme && $style[insightState.maskTheme]]"
            @click="handleMaskClick"
        ></div>
        <iframe
            v-if="loadIframe"
            v-show="visibleIframe && !insightState.iframeLoading"
            ref="InsightIframe"
            width="100%"
            height="100%"
            data-hj-allow-iframe
            :class="$style.iframe"
            :src="iframeDefaultSrc"
        ></iframe>
        <!--热图特殊对应工具弹框-->
        <HeatmapCode
            v-if="heatmapCodeState.dialogVisible"
            :visible="heatmapCodeState.dialogVisible"
            :current-url="heatmapCodeState.currentUrl"
            @close="heatmapCodeState.dialogVisible = false"
        >
        </HeatmapCode>
        <!--热图校验布码失败弹窗-->
        <HeatmapVerify
            v-if="heatmapVerifydialogVisible"
            :visible="heatmapVerifydialogVisible"
            @close="heatmapVerifydialogVisible = false"
        >
        </HeatmapVerify>
        <!-- 写入Url打开热图弹窗 -->
        <ViewHeatmapDialog v-if="heatmapViewDialogVisible"></ViewHeatmapDialog>
    </div>
</template>

<script lang="ts" setup>
import HeatmapCode from '@components/heatmap-code/HeatmapCode.vue';
import HeatmapVerify from '@master/views/setup/HeatmapVerify.vue';
import { isEqual } from 'lodash-es';
import { useRoute, useRouter } from 'vue-router';
import { watch, onMounted, onUnmounted, computed, ref, reactive, nextTick } from 'vue';
import usePlanLimit from '@master/hooks/service/usePlanLimit/usePlanLimit';
import { logout } from '@master/hooks/service/useLogin';
import intercomService from '@assets/services/intercom.service';
import analyticsService from '@assets/services/analytics.service';
import { usePermission } from '@models/profile/useProfilePermission';
import { useProfileList, Profile } from '@models/profile/profileList';
import { useAccountInfo } from '@models/account/useAccountInfo';
import { usePtiUsage } from '@models/profile/usePtiUsage';
import { useNPPackageInfo } from '@models/package/package';
import { usePackageInfo } from '@models/package/usePackageInfo';
import { useProfileInfo, ProfileSettingMenu } from '@models/profile/useCurrentProfile';
import { useProfileSessionConfig } from '@models/profile/useProfileSessionTime';
import {
    useHeatmap,
    PteAutoSaveHistoryHeatmapConfigInput,
    PteShareHeatmapConfigInput
} from '@models/insight/heatmap';
import urlUtils from '@utils/url.utils';
import cloneUtils from '@utils/clone.utils';
import cookieUtils from '@utils/cookie.utils';
import { ptEncodeURI } from '@utils/common.util';
import permissionUtils from '@utils/permission.utils';
import useEventBus from '@assets/plugins/eventBus';
import baseConfig from '@config/base.config';
import { getLoadingTips } from '@config/loadingTips.config';
import { setHeatMap, addProtocol } from '@models/experience/useHeatmap';
import { useI18n } from 'vue-i18n';
import { cloneDeep } from 'lodash-es';
import dateUtils from '@utils/date.utils';
import { setPtiTimeInfoToLocalStorage, clearPtiTimeInfo } from '@master/utils/account.util';
import useShowAuthDialog from '@master/hooks/service/useShowAuthDialog';
import { useGuideStore } from '@master/store/guide.store';
import ViewHeatmapDialog from '@/pages/master/components/dialog/ViewHeatmapDialog.vue';
import { storeToRefs } from 'pinia';

type Postmessage = {
    command: string;
    message: any;
};

type EventData = {
    data: Postmessage;
    origin: string;
};

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { eventBus } = useEventBus();
const { account } = useAccountInfo();
const { permissions } = usePermission();
const { getPtiUsage } = usePtiUsage();
const { showUpGradeDialog, checkAuth } = usePlanLimit();
const { checkAuth: checkProductAuth } = useShowAuthDialog();
const { profileList, profileListWithoutDemo, getProfileList } = useProfileList();
const { profileNpPackageInfo, getProfileNpPackageInfo } = useNPPackageInfo();
const { getProfileSessionConfig, saveProfileSessionConfig } = useProfileSessionConfig();
const { getProfilePackageInfo } = usePackageInfo();
const {
    profileInfo,
    profileCurrentMenu,
    allProductList,
    profileProductMenuList,
    setProfileProductMenu,
    checkActiveProfile,
    getInsightRoleList
} = useProfileInfo();
const {
    createAutoSaveHistoryHeatmapConfig,
    createShareHeatmapConfig,
    getShareHeatmapConfigByUrl,
    getAutoSaveHistoryHeatmapConfigByUrl
} = useHeatmap();

const { conversionDialogVisible } = storeToRefs(useGuideStore());
const { getStepConfig, updateStepsStatus, updateGuideState, hideGuidePopover, showGuidePopover } =
    useGuideStore();

const domain: string = urlUtils.getDomain(location.host);
const isEnRegion = location.hostname?.indexOf('com') !== -1;
const reloadMaxLen = 3;

const heatmapCodeState = reactive({
    dialogVisible: false,
    currentUrl: '' // 需要操作的页面url
});
const InsightWrap = ref();
const InsightIframe = ref();
const InsightIframeHost = `${location.protocol}//${
    isEnRegion
        ? import.meta.env.VITE_INSIGHT_SERVER_URL_EN
        : import.meta.env.VITE_INSIGHT_SERVER_URL
}`;
const iframeDefaultSrc = ref(
    InsightIframeHost +
        '/loading.html?language=' +
        (account.value?.language?.toLocaleLowerCase() || (isEnRegion ? 'en' : 'jp'))
);

const heatmapVerifydialogVisible = ref(false);
const heatmapViewDialogVisible = ref(false);

let nIntervId: any = null;
const insightState = reactive<{
    reloadNums: number;
    currentProfile: Profile | null;
    insightLastPath: string;
    maskTheme: string;
    iframeLoading: boolean;
    iframeLoaded: boolean;
    insightNextRoute: any;
    needUpdatePackageInfo: boolean;
}>({
    reloadNums: 0, //insight iframe加载失败，重新加载次数。每次加载成功重置为0
    currentProfile: null,
    maskTheme: '',
    insightLastPath: '',
    iframeLoading: true,
    iframeLoaded: false,
    insightNextRoute: null,
    needUpdatePackageInfo: false
});

const loadingTips = computed(() => {
    return getLoadingTips('insight', t) || [];
});

const iframeDefaultPath = computed(() => {
    return insightState.currentProfile?.isActived === 1
        ? '/datacenter/overview'
        : '/datacenter/onboarding';
});

// 是否渲染Iframe
const loadIframe = computed(() => {
    return (
        router.currentRoute.value.name !== 'InsightPlan' &&
        (insightState.iframeLoaded || !!insightState.currentProfile)
    );
});

// 是否显示Iframe
const visibleIframe = computed(() => {
    return router.currentRoute.value.name === 'Insight';
});

onMounted(() => {
    window.addEventListener('message', handleReceiveMessage, false);
    window._showHeatmapCode = showHeatmapCode;
    eventBus.$on('mainResize', handleResize);
    eventBus.$on('insightRouteChange', handleRouteChangeByMenu);
    eventBus.$on('applyTrial', handleApplyTrial);
    eventBus.$on('popstate', handlePopstate, false);
    eventBus.$on('changeProfileMenuItem', handleChangeMenu, false);
    eventBus.$on('openLandingPageHeatmap', handleLandingPageHeatmapOpen, false);
    eventBus.$on('openHeatmapDialog', openHeatmapDialog, false);
    eventBus.$on('countDownClick', () => {
        sendPostMessage('countDownClick');
    });
    eventBus.$on('insightChangeLanguage', handleChangeLanguage, false);
    eventBus.$on('updateNPPackageInfo', handleUpdatePackageInfo, false);
    eventBus.$on('resizeOnboTip', handleResizeOnboTip, false);
    eventBus.$on('logout', handleLogout, false);
    eventBus.$on('showConversionDialog', showConversionDialog, false);
    eventBus.$on('goToConversionAndShowDialog', goToConversionAndShowDialog, false);
    eventBus.$on('goToDataCenter', goToDataCenter, false);
    eventBus.$on('openViewHeatmapDialog', openViewHeatmapDialog, false);
    eventBus.$on('closeViewHeatmapDialog', closeViewHeatmapDialog, false);
    eventBus.$on('GuideAddHeatmapPage', GuideAddHeatmapPage, false);
    // 设置默认档案
    profileListWithoutDemo.value &&
        profileListWithoutDemo.value?.[0] &&
        !insightState.currentProfile &&
        updateState('currentProfile', getLastInsightProfile(profileListWithoutDemo.value));
});

onUnmounted(() => {
    window.removeEventListener('message', handleReceiveMessage, false);
    eventBus.$off('mainResize', handleResize);
    eventBus.$off('insightRouteChange', handleRouteChangeByMenu);
    eventBus.$off('applyTrial', handleApplyTrial);
    eventBus.$off('popstate', handlePopstate, false);
    eventBus.$off('changeProfileMenuItem', handleChangeMenu, false);
    eventBus.$off('openLandingPageHeatmap', handleLandingPageHeatmapOpen, false);
    eventBus.$off('countDownClick');
    eventBus.$off('openHeatmapDialog', openHeatmapDialog, false);
    eventBus.$off('insightChangeLanguage', handleChangeLanguage, false);
    eventBus.$off('updateNPPackageInfo', handleUpdatePackageInfo, false);
    eventBus.$off('resizeOnboTip', handleResizeOnboTip, false);
    eventBus.$off('logout', handleLogout, false);
    eventBus.$off('showConversionDialog', showConversionDialog, false);
    eventBus.$off('goToConversionAndShowDialog', goToConversionAndShowDialog, false);
    eventBus.$off('goToDataCenter', goToDataCenter, false);
    eventBus.$off('openViewHeatmapDialog', openViewHeatmapDialog, false);
    eventBus.$off('closeViewHeatmapDialog', closeViewHeatmapDialog, false);
    eventBus.$off('GuideAddHeatmapPage', GuideAddHeatmapPage, false);
});

// 切换档案
watch(
    () => profileInfo.value,
    (newProfile, oldProfile) => {
        newProfile &&
            !isEqual(newProfile, oldProfile) &&
            !isEqual(newProfile, insightState.currentProfile) &&
            canUseInsight(newProfile) &&
            (setProfileProductMenu(), setCurrentInsightProfile(newProfile));
    }
);

// 切换导航
watch(
    () => profileCurrentMenu.value,
    (menuItem, oldMenuItem) => {
        if (menuItem?.key && menuItem?.key !== oldMenuItem?.key) {
            if (
                !(menuItem.pathStartWith || []).some(pathKey =>
                    route.params?.path?.includes(pathKey)
                )
            ) {
                handleRouteChangeByMenu(menuItem);
            }
        }
    }
);

// 切换路由
watch(
    () => visibleIframe.value,
    (val, oldVal) => {
        if (val && !oldVal) {
            const currentPath = getRouteParams();
            if (!currentPath && insightState.insightLastPath) {
                router.push(insightState.insightLastPath);
            } else {
                const routerName =
                    ProfileSettingMenu.insight?.find(
                        item => decodeURIComponent(currentPath).indexOf(item.pathStartWith) !== -1
                    )?.routerName || profileCurrentMenu.value?.routerName;
                sendPostMessage('InsightChangeRoute', {
                    route: {
                        name: routerName
                    }
                });
            }
        }
        handleResize();
    }
);

// 监听套餐更改, 通知pti
watch(
    () => profileNpPackageInfo.value,
    (newVal, oldVal) => {
        // 初次通信时 套餐更新后通知pti更新
        if (newVal && !oldVal && insightState.needUpdatePackageInfo) {
            handleUpdatePackageInfo();
            updateState('needUpdatePackageInfo', false);
        }

        // 产品套餐权限变更时（不比较用量变化） 通知pti更新
        if (oldVal && newVal) {
            // 排除用量的比较 防止pti里的监听刷新
            const newPackageInfo = cloneDeep(newVal);
            const oldPackageInfo = cloneDeep(oldVal);
            delete newPackageInfo.commonPrivilegeUsage;
            delete newPackageInfo.ptiPrivilegeUsage;
            delete oldPackageInfo.commonPrivilegeUsage;
            delete oldPackageInfo.ptiPrivilegeUsage;
            if (!isEqual(newPackageInfo, oldPackageInfo)) {
                handleUpdatePackageInfo();
            }
        }
    },
    {
        immediate: true
    }
);

/**
 * 调起热图特殊对应工具的弹框
 */
function showHeatmapCode(url: string) {
    if (!url) {
        return;
    }
    heatmapCodeState.currentUrl = url;
    heatmapCodeState.dialogVisible = true;
}

function clearCurrentInsightProfile() {
    updateState('iframeLoading', true);
    updateState('iframeLoaded', false);
    updateState('currentProfile', null);
    updateState('insightLastPath', '');
}

function openHeatmapDialog() {
    loadUntilPageReady(200, function () {
        sendPostMessage('InsightCreateHeatmap');
    });
}

function loadUntilPageReady(maxPendingTime: number, callback: any) {
    let num = 1;

    const intval = setInterval(() => {
        num++;
        if (insightState.iframeLoaded) {
            callback && callback();
            clearInterval(intval);
        }
        if (num > maxPendingTime) {
            clearInterval(intval);
        }
    }, 100);
}

function canUseInsight(profile?: Profile) {
    return profile?.pteEnabled === 1;
}

function updateState(key: string, val: any) {
    if (Object.prototype.hasOwnProperty.call(insightState, key)) {
        (insightState as any)[key] = val;
    }
}

function reloadIframe() {
    insightState.currentProfile && setCurrentInsightProfile(insightState.currentProfile);
}

function getRouteParams() {
    let hash = route?.path?.split('insight')?.[1] || '';
    if (!hash && profileCurrentMenu.value) {
        const { pathStartWith, routerPath } = profileCurrentMenu.value;
        hash = `/${pathStartWith?.[0]}/${routerPath}`;
    }
    return hash;
}

function getLastInsightProfile(profileList: Array<Profile>) {
    const lastProfileId = cookieUtils.getItem(baseConfig.LAST_PROFILE_ID);
    const lastProfile = profileList.find((profile: Profile) => profile.sid === lastProfileId);
    const currentProfile =
        route.query.sid && profileList.find((profile: Profile) => profile.sid === route.query.sid);
    return currentProfile && canUseInsight(currentProfile)
        ? currentProfile
        : canUseInsight(lastProfile)
        ? lastProfile
        : profileList.find(profile => canUseInsight(profile));
}

function getRoutePathByPTIRouteState(to: any, from?: any) {
    const { name: fromName } = from || { name: '' };
    if (fromName === 'main.pagescene.heatmap') {
        localStorage.removeItem('ptiLocalStorage');
    }

    const { name: toName, url: toUrl, params } = to;
    const [menu, subMenu] = toName.split('.');
    const url = toUrl.replace(/:menu|:submenu/g, (a: string, b: string) => {
        if (a === ':menu') {
            return params.menu;
        }
        if (a === ':submenu') {
            return params.submenu;
        }
    });
    return `/${insightState.currentProfile?.sid}/insight/${subMenu}${url}`;
}

/**
 * 购买套餐成功后 需要传给pte新的packageInfo
 */
function handleUpdatePackageInfo() {
    sendPostMessage(
        'InsightUpdatePackageInfo',
        JSON.stringify({ packageInfo: profileNpPackageInfo.value })
    );
}

function handleLogout() {
    sendPostMessage('logout');
}

/**
 * 默认加载/切换档案
 * @param profile
 * @returns
 */
function setCurrentInsightProfile(profile?: Profile) {
    if (!profile || !canUseInsight(profile)) {
        return;
    }

    clearPtiTimeInfo(); // 切换档案清空pti的时间缓存，即会重置为过去7天

    updateState('iframeLoading', true);
    updateState('iframeLoaded', false);
    updateState('currentProfile', null);
    updateState('insightLastPath', '');

    // 切换档案时，iframe重新加载。避免快速切换档案造成档案不同步
    setTimeout(() => {
        insightState.currentProfile = cloneUtils.deep(profile);
    }, 50);
}

function sendPostMessage(command: string, message: any = {}) {
    try {
        InsightIframe.value?.contentWindow?.postMessage?.(
            {
                command,
                message
            },
            InsightIframeHost
        );
    } catch (e) {}
}

function handleChangeMenu(menuItem: any) {
    if (menuItem.productKey === 'insight') {
        insightState.iframeLoaded
            ? handleRouteChangeByMenu(menuItem)
            : updateState('insightNextRoute', menuItem);
    }
}
function handleChangeLanguage(language?: string) {
    if (!language) {
        return;
    }
    sendPostMessage('InsightChangeLanguage', { language });
}

/**
 * 打开LP页面热图
 */
function handleLandingPageHeatmapOpen(obj: {
    pid: string;
    url: string;
    title: string;
    smartDate: any;
}) {
    clearInterval(nIntervId);
    nIntervId = setInterval(() => {
        if (insightState.iframeLoaded) {
            clearInterval(nIntervId);
            sendPostMessage('openHeatmap', {
                smartDate: obj.smartDate,
                stateParams: {
                    rangetype: 'page',
                    rangeurl: obj.url,
                    rangetitle: obj.title,
                    rangeparam: obj.pid,
                    optimizely: '',
                    parameter: { merge: { url: obj.url, paramlist: [] } }
                }
            });
        }
    }, 100);
}

function handleRouterChangeStart({ firstChangeFlag, to, from }: any) {
    // 初次切换路由，需通信发送最新的路由信息。避免全部进入默认路由
    if (firstChangeFlag) {
        let nextRouteName =
            (insightState.insightNextRoute?.productKey === 'insight' &&
                insightState.insightNextRoute?.routerName) ||
            profileCurrentMenu.value?.routerName ||
            to.name;
        if (
            insightState.currentProfile?.isVirtual &&
            baseConfig.VIRTUAL_PROFILE_HIDE_MENUS.some(item => {
                return nextRouteName.indexOf(item) !== -1;
            })
        ) {
            // 虚拟档案 隐藏了 profile basic、 campaign 和traffic 的设置路由
            nextRouteName = 'main.datacenter.overview';
        }

        updateState('insightNextRoute', null);
        sendPostMessage('InsightStateChangeStartCallBack', {
            name: nextRouteName
        });
    }

    updateState('reloadNums', 0);
    updateState('iframeLoaded', true);
}

function handleRouterChangeSuccess({ to, from }: any) {
    updateState('insightLastPath', getRoutePathByPTIRouteState(to, from));
    updateState('iframeLoading', false);
    const { name, url, title } = to;
    const isToMainController = /^main/.test(name);

    if (name === '404') {
        handleRouteChangeByMenu({
            productKey: profileCurrentMenu.value?.productKey,
            routerName: 'main.datacenter'
        });
    } else if (visibleIframe.value) {
        // 更新基座路由
        if (isToMainController) {
            router.push({
                path: insightState.insightLastPath,
                query: route.query
            });

            // 进入热图列表页
            if (insightState.insightLastPath.endsWith('pagescenelist')) {
                showPopup();
            }
        }

        handleResize();
        title && (document.title = title);
    }

    if (to.name === 'main.datacenter.overview') {
        !getStepConfig('startGuide_dataCenter')?.done &&
            updateStepsStatus(['startGuide_dataCenter']);
    }
}

/**
 * 弹出热图完全放开后，针对当前为新档案，且有老档案的情况下弹出popup说明
 * 新档案：弹出热图完全放开后创建的档案
 * 老档案：弹出热图完全放开前创建的档案
 */
function showPopup() {
    const heatmapOpenTime = import.meta.env.VITE_HEATMAP_OPEN_TIME as unknown as number;
    const curProfileIsNewVer =
        profileInfo.value?.createTime && profileInfo.value?.createTime >= heatmapOpenTime;

    if (curProfileIsNewVer) {
        // 是否有老档案
        const hasOldVerProfile = profileList.value?.some(
            profile => profile.createTime && profile.createTime < heatmapOpenTime
        );
        hasOldVerProfile &&
            window.ptengine?.showEngagement?.(import.meta.env.VITE_HEATMAP_POPUP_ID);
    }
}

function handleRouteChangeByMenu({ productKey, routerName, params }: any) {
    productKey === 'insight' &&
        loadIframe.value &&
        sendPostMessage('InsightChangeRoute', {
            route: {
                name: routerName,
                params: params
            }
        });
}

function handleIframeClick() {
    eventBus.$emit('iFrameClick');
    // 关闭 guide 弹框
    hideGuidePopover();
}

function handleMaskClick() {
    sendPostMessage('popupBgClick');
}

function handleApplyTrial() {
    sendPostMessage('applyTrial');
}

function handleResize() {
    setTimeout(function () {
        const topHeight = InsightWrap.value?.getBoundingClientRect()?.top;
        InsightIframe.value &&
            (InsightIframe.value.style.height =
                document.documentElement.clientHeight - topHeight + 'px');
        eventBus.$emit('resizeTip');
    }, 0);
}

function handleShowInterCom() {
    intercomService?.contact();
}

function handleShowSetup() {
    eventBus.$emit('showSetup');
}

function handleShowVerify() {
    eventBus.$emit('showVerify');
}

function handlePopstate() {
    const uid = account.value?.ptiUid;
    const reg = new RegExp(
        `^${location.protocol}//${location.host}/${insightState.currentProfile?.sid}/insight`
    );
    const hash =
        location.href.replace(reg, '') ||
        (insightState.currentProfile?.isActived === 1
            ? '/datacenter/overview'
            : '/datacenter/onboarding');
    sendPostMessage('onpopstate', {
        uid,
        profile: JSON.parse(JSON.stringify(insightState.currentProfile)),
        path: `${InsightIframeHost}/?uid=${uid}&siteid=${insightState.currentProfile?.siteid}#${hash}`
    });
}

function handlePopupToggle({ theme }: { theme?: 'white' | 'black' }) {
    theme && (insightState.maskTheme = theme);
}

function handleResizeOnboTip(onboType) {
    sendPostMessage('InsightResizeOnboTip', onboType);
}

function getPtiTimeInfo() {
    const timeInfo = localStorage.getItem(baseConfig.PTI_TIME_INFO);
    if (timeInfo) {
        return timeInfo;
    }
    // 初始默认过去七天（包含今天）
    const { startTime, endTime } = dateUtils.getDate(
        'past',
        insightState.currentProfile?.timeZone,
        {
            value: 7,
            isIncludeToday: true
        }
    );
    const date = {
        starttime: dateUtils.getTimeStampWithTimezone(
            startTime as string,
            insightState.currentProfile?.timeZone as string
        ),
        endtime: dateUtils.getTimeStampWithTimezone(
            endTime as string,
            insightState.currentProfile?.timeZone as string
        ),
        starttimestr: startTime as string, // "YYYY/MM/DD"
        endtimestr: endTime as string
    };

    setPtiTimeInfoToLocalStorage(date);
    return JSON.stringify(date);
}

function rejectTip(res: any) {
    if (!res.hasCode && !res.sid) {
        heatmapVerifydialogVisible.value = true;
    }
}

function showConversionDialog() {
    sendPostMessage('ShowConversionDialog');
    updateGuideState('conversionDialogVisible', false);
}

function goToConversionAndShowDialog() {
    const product = allProductList.value?.find(product => product.key === 'insight');
    if (!product || !profileInfo.value) {
        return;
    }
    checkProductAuth(product, profileInfo.value, function () {
        const currentProductMenu = profileProductMenuList.value?.insight?.find(
            productMenu => productMenu.key === 'conversion'.toLocaleUpperCase()
        );
        setProfileProductMenu(currentProductMenu);
        router.push({
            name: product.routerName,
            params: {
                sid: profileInfo.value?.sid || ''
            }
        });

        // 如果insight当前就在conversion onboarding页，不会走pti conversion onboarding的controller, 则直接通知pti展示设置转化的弹框
        if (insightState.insightLastPath.includes('/insight/conversion/onboarding')) {
            showConversionDialog();
        }
    });
}

function goToDataCenter() {
    const product = allProductList.value?.find(product => product.key === 'insight');
    if (!product || !profileInfo.value) {
        return;
    }
    checkProductAuth(product, profileInfo.value, function () {
        const currentProductMenu = profileProductMenuList.value?.insight?.find(
            productMenu => productMenu.key === 'datacenter'.toLocaleUpperCase()
        );
        setProfileProductMenu(currentProductMenu);
        router.push({
            name: product.routerName,
            params: {
                sid: profileInfo.value?.sid || ''
            }
        });
    });
}

function openViewHeatmapDialog() {
    heatmapViewDialogVisible.value = true;
}

function closeViewHeatmapDialog() {
    heatmapViewDialogVisible.value = false;
}

function GuideAddHeatmapPage(data: { preUrl: string; url: string }) {
    sendPostMessage('GuideAddHeatmapPage', data);
}

async function handleReceiveMessage(event: EventData) {
    const { command, message } = event.data || {};
    const redirectHash = getRouteParams() || iframeDefaultPath.value;

    switch (command) {
        case 'InsightStart':
            sendPostMessage('InsightStartCallBack', {
                uid: account.value?.id,
                email: account.value?.email,
                password: account.value?.ptiPwd,
                sid: insightState.currentProfile?.sid,
                siteid: insightState.currentProfile?.siteid,
                token: cookieUtils.getItem(baseConfig.ACCOUNT_TOKEN),
                redirectHash
            });
            break;
        case 'InsightInitState': {
            const canAutoSaveHistoryHeatmap =
                permissions.value &&
                permissionUtils.includePermissionWithAccountInfo(['autoSaveHistoryHeatmap'], {
                    permissionIdList: permissions.value.permissionIdList,
                    permissionDict: permissions.value.permissionDict
                });
            const canSetShareHeatmapDataFilter =
                permissions.value &&
                permissionUtils.includePermissionWithAccountInfo(['enableShareHeatmapDataFilter'], {
                    permissionIdList: permissions.value?.permissionIdList,
                    permissionDict: permissions.value?.permissionDict
                });
            const canUseFirstScreenLoss =
                permissions.value &&
                permissionUtils.includePermissionWithAccountInfo(['firstScreenLoss'], {
                    permissionIdList: permissions.value?.permissionIdList,
                    permissionDict: permissions.value?.permissionDict
                });
            const canOnlyUseWebviewHeatmap =
                permissions.value &&
                permissionUtils.includePermissionWithAccountInfo(['canOnlyUseWebviewHeatmap'], {
                    permissionIdList: permissions.value?.permissionIdList,
                    permissionDict: permissions.value?.permissionDict
                });
            insightState.needUpdatePackageInfo = !profileNpPackageInfo.value; // @通信时如果packageInfo还没值 则需监听通知pti
            sendPostMessage(
                'InsightInitStateCallBack',
                JSON.stringify({
                    accountInfo: JSON.parse(JSON.stringify(Object.assign({}, account.value))),
                    profileInfo: JSON.parse(JSON.stringify(insightState.currentProfile ?? {})),
                    packageInfo: profileNpPackageInfo.value,
                    localStorageInfo:
                        redirectHash?.indexOf('/pagescene/heatmap') !== -1
                            ? cookieUtils.getItem('ptiLocalStorage') || null
                            : null,
                    localStorageTimeInfo: getPtiTimeInfo(),
                    language: account.value?.language?.toLocaleLowerCase(),
                    canAutoSaveHistoryHeatmap,
                    canSetShareHeatmapDataFilter,
                    canUseFirstScreenLoss,
                    canOnlyUseWebviewHeatmap,
                    useHashParam: insightState.currentProfile?.useHashParam as string,
                    cookieInfo: {
                        [baseConfig.SUPER_ACCOUNT]: cookieUtils.getItem(baseConfig.SUPER_ACCOUNT),
                        [baseConfig.MICROSOFT_AUTH_KEY]: cookieUtils.getItem(
                            baseConfig.MICROSOFT_AUTH_KEY
                        )
                    },
                    roleModuleList: getInsightRoleList()
                })
            );
            break;
        }
        case 'InsightMainControllerStart':
            sendPostMessage(
                'InsightMainControllerStartCallBack',
                JSON.stringify({
                    profileInfo: JSON.parse(JSON.stringify(insightState.currentProfile ?? {}))
                })
            );
            break;
        case 'InsightStateChangeStart':
            handleRouterChangeStart(message);
            break;
        case 'InsightStateChangeSuccess':
            handleRouterChangeSuccess(message);
            handleChangeLanguage(account.value?.language);
            break;
        case 'HeatmapAddSuccess':
            eventBus.$emit('HeatmapAddSuccess');
            break;
        case 'InsightDocumentClick':
            handleIframeClick();
            break;
        case 'InsightResize':
            // handleIframeResize(message);
            break;
        case 'refresh':
            location.reload();
            break;
        case 'InsightToggleModal':
            break;
        case 'InsightLogout': // 因特殊原因导致pti退出的
            // cookie超大导致431错误，nginx跳转至logout.html。前端清除cookie退出
            if (message?.position === 'logout.html') {
                cookieUtils.clearCookies();
                logout('no_token', 'insight_iframe');
                return;
            }

            if (insightState.reloadNums <= reloadMaxLen) {
                updateState('reloadNums', insightState.reloadNums + 1);
                reloadIframe();
            } else {
                // 失败超过3次，则清空insight信息，跳转至档案列表
                clearCurrentInsightProfile();
                updateState('reloadNums', 0);
                visibleIframe.value &&
                    router.push({
                        name: 'ProfileList'
                    });
                throw new Error(
                    `[Ptengne/Insight] -- insight reload more than five times. logout position: ${message?.position}, href: ${message.url}`
                );
            }
            break;
        case 'InsightShowApplyBtn': // 显示pti 的申请freeTrial按钮
            eventBus.$emit('showApplyBtn');
            break;
        case 'InsightShowApplyCountDown': // 显示 pti AdvanceTrial 倒计时提示
            eventBus.$emit('showApplyCountDown', message.countDown);
            break;
        case 'InsightHideApplyTrial': // 隐藏pti AdvanceTrial 按钮&倒计时
            eventBus.$emit('hideApplyTrial');
            break;
        case 'InsightGoChangePlan': // 跳转到changePlan页
            router.push({
                name: 'InsightPlan',
                params: {
                    menu: 'changeplan'
                }
            });
            break;
        case 'InsightGetLocalStorageInfo':
            cookieUtils.setItem({
                sKey: 'ptiLocalStorage',
                sValue: JSON.stringify(message.localStorageInfo),
                sDomain: domain
            });
            break;
        case 'InsightUpdateProfile':
            if (message.sid === insightState.currentProfile?.sid) {
                getProfileList();
            }
            break;
        case 'InsightDeleteProfile':
        case 'InsightDeleteUser': //退出协作，管理员踢出自己
            if (profileList.value !== undefined) {
                getProfileList();
            }
            clearCurrentInsightProfile();
            router.push({ name: 'ProfileList' });
            getProfileList();
            break;
        case 'InsightTogglePopup':
            handlePopupToggle(message);
            break;
        case 'InsightShowIntercom':
            handleShowInterCom();
            break;
        case 'InsightGotoSetup':
            handleShowSetup();
            break;
        case 'InsightGotoVerify':
            handleShowVerify();
            break;
        case 'InsightUsageRevalidate': // pti里增删 converion heatmap event时通知外层路由变更（onboard和overview的切换）
            getPtiUsage();
            getProfileList();
            break;
        case 'addConversionSuccess':
            !getStepConfig('startGuide_goal')?.done &&
                updateStepsStatus(['startGuide_goal'])?.then(() => showGuidePopover());
            break;
        case 'InsightReachLimit':
        case 'InsightCheckAuthAndShowDialog': {
            const res = checkAuth(Object.assign({}, message, { productType: 'pti' }));
            if (res) {
                sendPostMessage('authCheckResolve', res);
                if (command !== 'InsightReachLimit') {
                    !res.effective &&
                        showUpGradeDialog({
                            limitType: res.authKey,
                            privilege: res.privilege,
                            product: 'ptx'
                        });
                }
            }
            break;
        }
        case 'InsightShowAuthDialog': {
            const { limitType } = message;
            limitType && showUpGradeDialog({ limitType });
            break;
        }
        case 'InsightUpdateNPUsage':
            getProfilePackageInfo({
                sid: insightState.currentProfile?.sid
            });
            getProfileNpPackageInfo({
                sid: insightState.currentProfile?.sid
            });
            break;
        case 'InsightTrackEvent':
            visibleIframe.value && analyticsService.usePTX(message.name, message.property).track();
            break;
        case 'InsightOpenWebsite':
            eventBus.$emit('upgrade', {
                type: 'heatmapList'
            });
            break;
        case 'InsightGetProfileSessionConfig':
            getProfileSessionConfig(message.sid, res => {
                sendPostMessage(
                    'InsightGetProfileSessionConfigResolve',
                    res !== null
                        ? {
                              mobileSessionTimeout: res.mobileSessionTimeout,
                              pcSessionTimeout: res.pcSessionTimeout
                          }
                        : res
                );
            });
            break;
        case 'InsightSaveProfileSessionConfig':
            if (message?.profileId && message?.pcSessionTimeout && message?.mobileSessionTimeout) {
                saveProfileSessionConfig(
                    message,
                    res => {
                        sendPostMessage('InsightSaveProfileSessionConfigResolve', {
                            success: true
                        });
                    },
                    err => {
                        sendPostMessage('InsightSaveProfileSessionConfigResolve', {
                            success: false
                        });
                    }
                );
            }
            break;
        // 设置 历史热图自动保存
        case 'InsightCreateAutoSaveHistoryHeatmapConfig':
            createAutoSaveHistoryHeatmapConfig(
                message as PteAutoSaveHistoryHeatmapConfigInput,
                function () {
                    sendPostMessage(
                        'InsightCreateAutoSaveHistoryHeatmapConfigResolve',
                        JSON.stringify({
                            status: 'success'
                        })
                    );
                },
                function () {
                    sendPostMessage(
                        'InsightCreateAutoSaveHistoryHeatmapConfigResolve',
                        JSON.stringify({
                            status: 'failed'
                        })
                    );
                }
            );
            break;
        // 获取 历史热图自动保存配置
        case 'InsightGetAutoSaveHistoryHeatmapConfigByUrl':
            getAutoSaveHistoryHeatmapConfigByUrl(
                message.shareLink,
                function (config) {
                    sendPostMessage(
                        'InsightGetAutoSaveHistoryHeatmapConfigByUrlResolve',
                        JSON.stringify({
                            status: 'success',
                            config
                        })
                    );
                },
                function () {
                    sendPostMessage(
                        'InsightGetAutoSaveHistoryHeatmapConfigByUrlResolve',
                        JSON.stringify({
                            status: 'failed'
                        })
                    );
                }
            );
            break;
        // 设置 分享热图（日历/历史热图）
        case 'InsightCreateShareHeatmapConfig':
            createShareHeatmapConfig(
                message as PteShareHeatmapConfigInput,
                function (res) {
                    sendPostMessage(
                        'InsightCreateShareHeatmapConfigResolve',
                        JSON.stringify({
                            status: 'success'
                        })
                    );
                },
                function () {
                    sendPostMessage(
                        'InsightCreateShareHeatmapConfigResolve',
                        JSON.stringify({
                            status: 'failed'
                        })
                    );
                }
            );
            break;
        // 获取 分享热图配置信息
        case 'InsightGetShareHeatmapConfigByUrl':
            getShareHeatmapConfigByUrl(
                message.shareLink,
                function (config) {
                    sendPostMessage(
                        'InsightGetShareHeatmapConfigByUrlResolve',
                        JSON.stringify({
                            status: 'success',
                            config
                        })
                    );
                },
                function () {
                    sendPostMessage(
                        'InsightGetShareHeatmapConfigByUrlResolve',
                        JSON.stringify({
                            status: 'failed'
                        })
                    );
                }
            );
            break;
        case 'InsightRunScript':
            if (message.code) {
                const head = document.getElementsByTagName('head')[0];
                const scriptTag = document.createElement('script');
                const scriptContentWrapper = `
                    try {
                        (function(){${message.code}})();
                    } catch (error) {
                        console.warn(error);
                    }`;
                scriptTag.innerHTML = scriptContentWrapper;
                head.appendChild(scriptTag);
                head.removeChild(scriptTag);
            }
            break;
        // 打开嵌入热图
        case 'InsightOpenHeatmap':
            const { overlayUrl } = message;
            const params = urlUtils.getUrlParams(overlayUrl);
            const token = params && params.ptengine_heatmap_token;
            if (!token) {
                return;
            }

            // 打开热图的同时校验url有没有布码
            checkActiveProfile(
                insightState.currentProfile?.sid as string,
                message.option.pageInfo?.currentUrl,
                () => {},
                rejectTip
            );
            const language = account.value?.language?.toLocaleLowerCase();
            const urlParam = `ptengine_heatmap_token=${token}${
                language ? '&pt_lang=' + language : ''
            }`;
            await setHeatMap(message.option, token).then((pageUrl?: string) => {
                if (pageUrl) {
                    const overlayUrl = urlUtils.urlInsertParam(
                        ptEncodeURI(addProtocol(pageUrl)), // url encode一层 是防止用户url上带有空格和%{\d}时 shopfiy网站访问报错。
                        urlParam,
                        profileInfo.value?.useHashParam
                    );

                    urlUtils.openUrlUseATag(overlayUrl);
                }
            });

            if (message.loadingData) {
                sendPostMessage('heatmapListOpenSuccess', {
                    ...message.loadingData
                });
            }
            break;

        case 'InsightShowOnboTip':
            const { type, clientData, isResize } = message;
            const { top: containerTop, left: containerLeft } =
                InsightWrap.value?.getBoundingClientRect() || {
                    top: 0,
                    left: 0
                };
            eventBus.$emit('showTipWithClientdata', {
                type,
                isResize,
                clientData: clientData && JSON.parse(clientData),
                containerClientData: {
                    top: containerTop,
                    left: containerLeft
                }
            });
            break;

        case 'InsightUpdateTime':
            // 更新pti时间缓存
            setPtiTimeInfoToLocalStorage(message);
            break;
        case 'ShowConversionDialog':
            if (conversionDialogVisible.value) {
                showConversionDialog();
            }
            break;
        default:
            break;
    }
}
</script>

<style lang="scss" module>
@import '@style/import.scss';
.insight {
    position: relative;

    &::before {
        content: '';
        width: 100%;
        height: 1px;
        position: absolute;
        top: 0;
        left: 0;
        z-index: $iframe-top-shadow-index;
        box-shadow: inset 0 1px 6px rgba(0, 0, 0, 0.1);
        visibility: hidden;
    }

    &.visible {
        min-height: calc(100vh - var(--page-header-height) - var(--page-submenu-height));

        &::before {
            visibility: visible;
        }
    }

    &.loaded {
        min-height: auto;
        min-width: $page-min-width;
    }
}

.mask {
    position: absolute;
    top: -100%;
    left: 0;
    bottom: 0;
    right: 0;
    z-index: $iframe-bg-index;

    &.white {
        background-color: rgba(255, 255, 255, 0.95);
    }
    &.black {
        opacity: 0.7;
        background-color: rgba(9, 30, 66, 0.8);
    }
}

.iframe {
    display: block;
    width: 100%;
    border: none;
    z-index: $iframe-index;
    position: relative;
}
</style>
